import React, { useContext, useRef } from 'react';
import HiddenGridRow from './HiddenGridRow';
import { useRowVisibility } from '../../Torpedo/BaseTorpedo/hooks/useRowVisibility';
import { GridElement, GridElementId } from '../types';
import { DuperbrowseItemId } from '../../Duperbrowse/redux/types';
import VisibleGridRow from './VisibleGridRow';
import OffsetTracking from '../../Torpedo/BaseTorpedo/_partials/OffsetTracking';
import LazyContext from '../../LazyLoadingImage/LazyContext';
import { LAZYLOAD_ROW_START } from '../../types';

export interface Props {
  rowIdx: number;
  start: number;
  end: number;
  elementWidth: number;
  elementHeight: number;
  elementSpacing: number;
  scrollOptims?: boolean;
  contentStrip?: React.ReactNode;
  withShadowSpace?: boolean;
  onOffsetReached?: (offset: number) => void;
  getDuperbrowseItemId?: (index: number) => DuperbrowseItemId | undefined;
  /** @deprecated */
  getElementId?: (index: number) => GridElementId;
  renderElement: (element: GridElement) => React.ReactNode;
  rowAlign?: 'left' | 'center' | 'right';
  lazyloadFromContext?: boolean;
}

const GridRow: React.FC<Props> = ({
  start,
  end,
  elementWidth,
  elementHeight,
  elementSpacing,
  scrollOptims,
  contentStrip,
  rowIdx,
  withShadowSpace,
  renderElement,
  getDuperbrowseItemId,
  onOffsetReached,
  rowAlign,
  lazyloadFromContext,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useRowVisibility(ref, { enabled: scrollOptims });
  const lazyValue = useContext(LazyContext);

  let rowStyle: React.CSSProperties = {};
  if (rowAlign) {
    const settingToCss = {
      left: 'flex-start',
      right: 'flex-end',
      center: 'center',
    };
    rowStyle = { display: 'flex', justifyContent: settingToCss[rowAlign] };
  }

  return (
    <LazyContext.Provider
      value={lazyloadFromContext ? lazyValue : rowIdx >= LAZYLOAD_ROW_START}
    >
      <OffsetTracking rowIdx={rowIdx} onOffsetReached={onOffsetReached}>
        <div ref={ref} style={rowStyle} data-testid="grid-row">
          {contentStrip}
          {isVisible ? (
            <VisibleGridRow
              start={start}
              end={end}
              elementWidth={elementWidth}
              elementHeight={elementHeight}
              elementSpacing={elementSpacing}
              withShadowSpace={withShadowSpace}
              renderElement={renderElement}
            />
          ) : (
            <HiddenGridRow
              start={start}
              end={end}
              getDuperbrowseItemId={getDuperbrowseItemId}
              marginTop={elementSpacing}
              height={elementHeight}
            />
          )}
        </div>
      </OffsetTracking>
    </LazyContext.Provider>
  );
};
GridRow.displayName = 'GridRow';

export default GridRow;
